/* cf-send-plans.js - Click Fencing, 2026-08-22
 *
 * A friendly, always-there way for a customer to hand us their plans and let us do the work.
 *
 * WHY
 * ---
 * The calculators are excellent when someone knows their run lengths. Plenty of people do not.
 * They have a builder's site plan, a pool certifier's sketch, or a photo of the backyard with
 * a tape measure in it - and no idea which of the four ranges they are even looking at. Until
 * now the only route for them was "Send us your plans" buried in the top nav, which points at
 * /pages/contact - a plain Shopify contact form that cannot take an attachment at all. So the
 * one customer who most needs help had the weakest path.
 *
 * WHAT IT DOES
 * ------------
 * A small pill sits bottom-left on every page. Opening it explains the offer in one sentence
 * and gives two ways through:
 *   - "Email your plans" - a mailto to enquiries@clickfencing.com.au with the subject and a
 *     short prompt list already written, so the customer only has to attach the file and hit
 *     send. Attachments work in email; they do not work in the Shopify contact form.
 *   - "Use the contact form" - /pages/contact, for anyone who would rather type than email.
 *
 * PLACEMENT
 * ---------
 * Bottom LEFT on purpose. The site's chat widget owns the bottom right (#qpw-panel), and two
 * floating buttons in the same corner is how a page starts feeling like a used-car lot.
 * z-index sits under modal layers so it can never cover a checkout dialog.
 *
 * Remembers nothing and stores nothing - no localStorage, no cookies. Dismissing it collapses
 * the panel for that page view only.
 */
(function () {
  'use strict';
  if (window.__cfSendPlansInstalled) return;
  window.__cfSendPlansInstalled = true;

  var EMAIL = 'enquiries@clickfencing.com.au';
  var SUBJECT = 'My pool fence plans - please work out my parts list';
  var BODY = [
    'Hi Click Fencing,',
    '',
    'Please work out what I need for my pool fence. My plans / measurements are attached.',
    '',
    'Name:',
    'Suburb:',
    'Best contact number:',
    'Roughly how many metres of fencing:',
    'Fence type I am after (frameless glass / blade / flat top / perfmesh / not sure):',
    'Any gates, and how many:',
    'Sloping ground or steps anywhere:',
    '',
    'Thanks.'
  ].join('\r\n');

  function mailto() {
    return 'mailto:' + EMAIL
      + '?subject=' + encodeURIComponent(SUBJECT)
      + '&body=' + encodeURIComponent(BODY);
  }

  function css() {
    if (document.getElementById('cf-send-plans-css')) return;
    var s = document.createElement('style');
    s.id = 'cf-send-plans-css';
    s.textContent = [
      '.cf-plans{position:fixed;left:2rem;bottom:2rem;z-index:900;font-family:Inter,sans-serif}',
      '@media (max-width:749px){.cf-plans{left:1.2rem;bottom:1.2rem}}',
      '.cf-plans__pill{display:inline-flex;align-items:center;gap:.8rem;background:#1C2228;color:#fff;',
      'border:0;border-radius:999px;padding:1.2rem 1.9rem;font-size:1.4rem;font-family:inherit;',
      'letter-spacing:.02em;cursor:pointer;box-shadow:0 6px 20px rgba(0,0,0,.22);',
      'transition:transform .15s ease,box-shadow .15s ease}',
      '.cf-plans__pill:hover{transform:translateY(-2px);box-shadow:0 10px 26px rgba(0,0,0,.28)}',
      '.cf-plans__pill svg{flex:0 0 auto}',
      '@media (max-width:480px){.cf-plans__pill{padding:1.1rem 1.5rem;font-size:1.3rem}}',
      '.cf-plans__panel{display:none;width:32rem;max-width:calc(100vw - 2.4rem);background:#fff;',
      'color:#2B2C2D;border-radius:14px;padding:2.2rem 2.2rem 2rem;box-shadow:0 14px 44px rgba(0,0,0,.24);',
      'position:relative}',
      '.cf-plans.is-open .cf-plans__panel{display:block}',
      '.cf-plans.is-open .cf-plans__pill{display:none}',
      '.cf-plans__close{position:absolute;top:.9rem;right:1.1rem;background:none;border:0;',
      'font-size:2.2rem;line-height:1;color:#8b8e91;cursor:pointer;padding:.2rem .6rem}',
      '.cf-plans__close:hover{color:#2B2C2D}',
      '.cf-plans__h{font-size:1.9rem;font-weight:600;margin:0 0 .9rem;padding-right:2rem}',
      '.cf-plans__p{font-size:1.4rem;line-height:1.6;color:#4a4c4e;margin:0 0 1.8rem}',
      '.cf-plans__primary{display:block;text-align:center;background:#1C2228;color:#fff;',
      'text-decoration:none;border-radius:8px;padding:1.25rem 1.6rem;font-size:1.45rem;',
      'letter-spacing:.02em;transition:opacity .15s ease}',
      '.cf-plans__primary:hover{opacity:.87;color:#fff}',
      '.cf-plans__alt{display:block;text-align:center;font-size:1.3rem;color:#5a5c5e;',
      'margin:1.2rem 0 0;text-decoration:underline}',
      '.cf-plans__alt:hover{color:#2B2C2D}',
      '.cf-plans__foot{font-size:1.2rem;color:#7a7c7e;margin:1.4rem 0 0;text-align:center}'
    ].join('');
    document.head.appendChild(s);
  }

  var ICON = '<svg width="17" height="17" viewBox="0 0 24 24" fill="none" stroke="currentColor" '
    + 'stroke-width="2" stroke-linecap="round" stroke-linejoin="round" aria-hidden="true">'
    + '<path d="M4 4h16v16H4z"/><path d="M4 9h16M9 9v11"/></svg>';

  function build() {
    css();
    var box = document.createElement('div');
    box.className = 'cf-plans';
    box.setAttribute('data-cf-plans', '');
    box.innerHTML =
      '<button class="cf-plans__pill" type="button" aria-expanded="false">' + ICON
        + '<span>Send us your plans</span></button>'
      + '<div class="cf-plans__panel" role="dialog" aria-label="Send us your plans">'
      + '<button class="cf-plans__close" type="button" aria-label="Close">×</button>'
      + '<p class="cf-plans__h">We’ll work out your parts list</p>'
      + '<p class="cf-plans__p">Send us your plans, a sketch, or just your measurements and '
      + 'we’ll work out exactly what you need — panels, posts, gates and every bracket '
      + '— and send back a fixed quote. No obligation.</p>'
      + '<a class="cf-plans__primary" href="' + mailto() + '">Email your plans →</a>'
      + '<a class="cf-plans__alt" href="/pages/contact">Or use the contact form</a>'
      + '<p class="cf-plans__foot">Attachments welcome — PDF, photo or screenshot.</p>'
      + '</div>';

    var pill = box.querySelector('.cf-plans__pill');
    pill.addEventListener('click', function () {
      box.classList.add('is-open');
      pill.setAttribute('aria-expanded', 'true');
    });
    box.querySelector('.cf-plans__close').addEventListener('click', function () {
      box.classList.remove('is-open');
      pill.setAttribute('aria-expanded', 'false');
    });
    document.addEventListener('keydown', function (e) {
      if (e.key === 'Escape') box.classList.remove('is-open');
    });
    document.body.appendChild(box);
    console.log('[cf-send-plans] installed');
  }

  function start() {
    if (document.querySelector('[data-cf-plans]')) return;
    build();
  }

  if (document.readyState === 'loading') document.addEventListener('DOMContentLoaded', start);
  else start();
})();
